import React, { useState } from "react";
import { Link } from "react-router-dom";
import BasicModal from "../../ui/BasicModal/BasicModal";
import { Radio, Space, Input, Button, Row, Col, Form, notification, Alert } from "antd";
import { httpService } from "../../base/services/httpService.service";
import AuthService from "../../base/services/authentication.service";
import { useProfileStore } from "../../pages/auth/authStore";
import { EntityTypeEnum, UserRolesEnum } from "../../utils/enums";
import { BillingCode } from "../Billing/billingCodes";
const { TextArea } = Input;

const SUBSCRIPTION_PATH = "/subscription-plans";

// Deleting an organization, or its owner's account, is refused while an annual
// plan is still committed (409 COMMITMENT_ACTIVE) or an ended one still owes
// money (409 OUTSTANDING_DEBT). Asking again changes neither.
const FINAL_REFUSALS = [BillingCode.COMMITMENT_ACTIVE, BillingCode.OUTSTANDING_DEBT];

// The answer a failure carries: an axios rejection's body, or a failed envelope
// itself (httpService hands a 2xx with isSuccess false to its error callback).
const answerOf = (failure) =>
   failure?.response?.data ?? (failure?.isSuccess === false ? failure : null);

// Why the server refused, in its own words: the date a commitment ends and the
// amount owed are already in them. Decided by the code, never by the text.
const readRefusal = (failure) => {
   const answer = answerOf(failure);
   return {
      code: answer?.code ?? null,
      message: answer?.errors?.[0] || answer?.message || "Something went wrong. Please try again"
   };
};

const DeactivateAccountModal = ({ isModalOpen, handleAccountModal }) => {
   const userId = AuthService.getUser().id;
   const organizationId = AuthService.getOrganizationId();
   const [isDeactivateAccount, setIsDeactivateAccount] = useState(false);
   const [deleteRefusal, setDeleteRefusal] = useState(null);
   const [form] = Form.useForm();
   const { organizationMemberId } = useProfileStore();
   const userRoleOrganization = AuthService.isAuthorized([UserRolesEnum.Organization]);
   const [value, setValue] = useState({
      partOne: null,
      partTwo: null
   });

   const isDeleteRefusedForGood =
      value.partOne === 1 && FINAL_REFUSALS.includes(deleteRefusal?.code);

   const closeModal = () => {
      setDeleteRefusal(null);
      handleAccountModal();
   };

   const onChange = (e) => {
      setValue({ ...value, partOne: e.target.value, partTwo: null });
      form.setFieldsValue({ reason: null, additionalReason: null, feedback: null });
   };

   const onChangeReason = (e) => {
      setValue({ ...value, partTwo: e.target.value });
      if (e.target.value !== 3) {
         form.setFieldsValue({ additionalReason: null });
      }
   };

   const deactivateAccountButton = () => {
      setIsDeactivateAccount(true);
      const entityType = userRoleOrganization
         ? `${organizationId}/toggleDeleteOrRestore?entityType=${EntityTypeEnum.Organization}`
         : `${organizationMemberId}/toggleDeleteOrRestore?entityType=${EntityTypeEnum.OrganizationMember}`;

      const url = userRoleOrganization
         ? `/Organizations/permanentlyDeleteOrg?organizationId=${organizationId}`
         : `/Account/permanentlyDeleteUser?userId=${userId}`;

      const deleteAccount = value.partOne === 1;
      if (deleteAccount) {
         setDeleteRefusal(null);
         httpService.delete(
            url,
            {},
            (response) => {
               if (response.isSuccess) {
                  notification.success({
                     message: userRoleOrganization
                        ? "Organization deleted successfully"
                        : "User deleted successfully",
                     description: userRoleOrganization
                        ? "The organization and all of its data have been deleted."
                        : "Your account has been deleted."
                  });

                  // Signing out reloads the page, so the notice gets a moment first.
                  setTimeout(() => {
                     AuthService.logout();
                  }, 1000);
               } else {
                  notification.error({ description: response.message });
               }
            },
            (error) => {
               // Shown in the modal, which stays open: a refused delete can still be
               // cancelled or turned into a deactivation.
               setDeleteRefusal(readRefusal(error));
               setIsDeactivateAccount(false);
            }
         );
      } else {
         httpService
            .put(`/DataCollection/${entityType}&isDeleted=${true}`, {}, (response) => {
               if (!response) return;
               notification.success({
                  description: "Deactivated successfully"
               });

               setTimeout(() => {
                  AuthService.logout();
               }, 1000);
            })
            .catch((error) => {
               notification.error({
                  message: "Oops! A problem has occurred!",
                  description: readRefusal(error).message
               });
            })
            .finally(() => {
               closeModal();
               setIsDeactivateAccount(false);
            });
      }
   };

   const getConditionalText = () => {
      const action = value.partOne;
      return action === 1 ? "deleting" : "deactivating";
   };

   return (
      <BasicModal isModalOpen={isModalOpen} handleCancel={closeModal} footerButtonCustomStyle>
         <Form onFinish={deactivateAccountButton} form={form}>
            <div className="mt-5 mb-8">
               <h4>Delete or deactivate your account</h4>
            </div>
            <p className="label-text mt-8 mb-5">
               We're sorry to see you go! To help us improve, would you mind telling us why you're{" "}
               {getConditionalText()} your account?
            </p>
            <>
               {/* Required: with neither picked, Continue used to deactivate, past the copy that
                   says what deactivating does to the plan. */}
               <Form.Item
                  name="deleteOrDeactivate"
                  rules={[{ required: true, message: "Please select an option" }]}
               >
                  <Radio.Group onChange={onChange} value={value.partOne}>
                     <Space direction="vertical">
                        <Radio value={1}>Delete my account</Radio>
                        <Radio value={2}>Deactivate my account</Radio>
                     </Space>
                  </Radio.Group>
               </Form.Item>
               {value.partOne === 1 ? (
                  <>
                     <p className="label-text mt-8 mb-5">
                        We're sorry to see you go! To help us improve, would you mind telling us why
                        you're deleting your account?
                     </p>
                     {userRoleOrganization ? (
                        <p className="label-text mb-5">
                           Deleting permanently removes the organization and all of its data:
                           workspaces, projects, surveys, responses, beneficiaries, campaigns and
                           files. Your account is deleted too, unless you own another organization.
                           This can't be undone. Your plan ends immediately, with no refund for the
                           rest of the current period. An annual plan can't be deleted before its
                           12-month commitment ends.
                        </p>
                     ) : null}
                     <Form.Item
                        name="reason"
                        rules={[{ required: true, message: "Please select an option" }]}
                     >
                        <Radio.Group onChange={onChangeReason} value={value.partTwo}>
                           <Space direction="vertical">
                              <Radio value={1}>I no longer use this service.</Radio>
                              <Radio value={2}>I'm concerned about my privacy.</Radio>
                              <Radio value={3}>I'm not satisfied with the service.</Radio>
                              <Radio value={4}>Other (please specify)</Radio>
                           </Space>
                        </Radio.Group>
                     </Form.Item>
                     {value.partTwo === 4 ? (
                        <Form.Item
                           name="additionalReason"
                           rules={[{ required: true, message: "Please type something here" }]}
                        >
                           <TextArea rows={2} placeholder="Type something here" />
                        </Form.Item>
                     ) : null}
                  </>
               ) : value.partOne === 2 ? (
                  <>
                     <p className="label-text mt-8 mb-5">
                        Thinking of taking a break? Deactivating your account is always an option.
                        We'd appreciate your feedback if you're willing to share why you're
                        considering this.
                     </p>
                     {userRoleOrganization ? (
                        <p className="label-text mb-5">
                           Deactivating hides the organization: members can't open it until you sign
                           in again. It doesn't cancel your plan: it keeps renewing and you'll keep
                           being charged. To stop paying, cancel your plan from the{" "}
                           <Link to={SUBSCRIPTION_PATH} onClick={closeModal}>
                              Subscription page
                           </Link>
                           .
                        </p>
                     ) : null}
                     <Form.Item
                        name="reason"
                        rules={[{ required: true, message: "Please select an option" }]}
                     >
                        <Radio.Group onChange={onChangeReason} value={value.partTwo}>
                           <Space direction="vertical">
                              <Radio value={1}>I'm just taking a break from this platform.</Radio>
                              <Radio value={2}>I'm not using this account anymore.</Radio>
                              <Radio value={3}>Other (please specify)</Radio>
                           </Space>
                        </Radio.Group>
                     </Form.Item>
                     {value.partTwo === 3 ? (
                        <Form.Item
                           name="additionalReason"
                           rules={[{ required: true, message: "Please type something here" }]}
                        >
                           <TextArea rows={2} placeholder="Type something here" />
                        </Form.Item>
                     ) : null}
                  </>
               ) : null}
               {value.partOne === 1 || value.partOne === 2 ? (
                  <>
                     <p className="label-text mt-8">
                        Is there anything we could have done to improve your experience? Please tell
                        us more about why you're deleting your account.
                     </p>
                     <Form.Item
                        name="feedback"
                        rules={[{ required: true, message: "Please type something here" }]}
                     >
                        <TextArea rows={2} placeholder="Type something here" />
                     </Form.Item>
                  </>
               ) : null}
               {value.partOne === 1 && deleteRefusal ? (
                  <Alert
                     type={deleteRefusal.code ? "warning" : "error"}
                     showIcon
                     message={
                        <>
                           {deleteRefusal.message}
                           {deleteRefusal.code === BillingCode.OUTSTANDING_DEBT ? (
                              <>
                                 {" "}
                                 <Link to={SUBSCRIPTION_PATH} onClick={closeModal}>
                                    Go to the Subscription page
                                 </Link>
                              </>
                           ) : null}
                        </>
                     }
                  />
               ) : null}
            </>
            <Row gutter={16} className="mt-8" justify="center" align="middle">
               <Col>
                  <Button
                     className="defaultButton"
                     onClick={closeModal}
                     disabled={isDeactivateAccount}
                  >
                     Cancel
                  </Button>
               </Col>
               <Col>
                  <Button
                     htmlType="submit"
                     className="primaryButton"
                     loading={isDeactivateAccount}
                     disabled={isDeleteRefusedForGood}
                  >
                     Continue
                  </Button>
               </Col>
            </Row>
         </Form>
      </BasicModal>
   );
};

export default DeactivateAccountModal;
